import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import shortUUID from 'short-uuid';
import debounce from 'lodash.debounce';
import { mdiCheckCircle } from '@mdi/js';
import { ValidateError } from '../ErrorMessage';
import { Tooltip } from '../Tooltip';
import './styles.css';
import Icons from '../Icon';

export const TextInput = ({
  error,
  color,
  className,
  large,
  noPadding,
  id,
  type,
  label,
  disabled,
  required,
  onChange,
  icon,
  iconPosition,
  iconChangeType,
  field,
  form,
  maxLength,
  tooltip,
  defaultFile,
  min,
  max,
  notInInterval,
  classNameInput,
  classNameNumberError,
  ...otherProps
}) => {
  const uuid = useMemo(() => shortUUID().generate(), []);
  const fileRef = useRef(null);
  const [showHidePassword, setShowHidePassword] = useState(false);
  const [iconShowHide, setIconShowHide] = useState('icon-eye');
  const [showCircle, setShowCircle] = useState(false);
  const [err, setError] = useState(false);

  useEffect(() => {
    if (showHidePassword) setIconShowHide('icon-eye-off');
    else setIconShowHide('icon-eye');
  }, [showHidePassword]);

  useEffect(() => {
    if (type === 'file' && defaultFile?.file) {
      const dataTransfer = new DataTransfer();

      for (let i = 0; i < defaultFile?.file.length; i += 1) {
        const file = defaultFile?.file.item(i);

        dataTransfer.items.add(file);
      }

      if (fileRef && fileRef.current && fileRef.current.files) {
        fileRef.current.files = dataTransfer.files;
      }
    }
  }, [defaultFile?.file, type]);

  const handleChange = (el) => {
    const val = el.target.value;
    if (onChange) {
      if (type === 'number' && !Number.isNaN(val)) {
        if (typeof min === 'number' && typeof max === 'number') {
          if (val >= min && val <= max) {
            onChange(el);
          } else if (notInInterval) {
            notInInterval();
          }
        } else {
          onChange(el);
        }
      } else {
        onChange(el);
      }
    }
  };

  const debouncedSave = useCallback(
    debounce((nextValue) => setError(nextValue), 2000),
    []
  );

  const handleError = (val) => {
    debouncedSave(!(val >= min && val <= max));
  };

  useEffect(() => {
    if (err && disabled) {
      setError(false);
    }
  }, [disabled, err]);

  return (
    <div className={className}>
      {label ? (
        <label
          className={`label
              ${disabled ? 'disabled' : ''} ${
            form && field
              ? form.errors[field.name] && form.touched[field.name]
                ? 'error'
                : ''
              : ''
          }`}
          htmlFor={id || uuid}
        >
          {label}
          {tooltip && (
            <Tooltip title={tooltip} arrow placement="right">
              ?
            </Tooltip>
          )}
        </label>
      ) : (
        ''
      )}
      <div
        className={`text-input-wrapper ${color || ''} ${
          label ? '' : 'no-label'
        }`}
      >
        {field ? (
          <>
            <input
              {...field}
              id={id || uuid}
              className={`text-input ${
                icon ? 'with-icon' : ''
              } ${`icon-${iconPosition}`} ${
                form
                  ? form.errors[field.name] && form.touched[field.name]
                    ? 'error'
                    : ''
                  : ''
              } ${large ? 'large' : ''} ${noPadding ? 'no_padding' : ''}`}
              type={
                type ||
                field.type ||
                (iconChangeType ? (showHidePassword ? 'text' : 'password') : '')
              }
              maxLength={maxLength || ''}
              required={required}
              disabled={disabled}
              {...otherProps}
              onChange={(e) => {
                if (onChange) {
                  return onChange(e);
                }
                return field.onChange(e);
              }}
            />
            {!iconChangeType && icon && (
              <i className={`${icon} ${iconPosition} ${color || ''}`} />
            )}
            {iconChangeType && (
              <i
                role="button"
                tabIndex={-1}
                className={`${iconShowHide} ${iconPosition} ${color || ''}`}
                onClick={() => setShowHidePassword(!showHidePassword)}
                aria-hidden="true"
              />
            )}
            {!form.isValid
              ? form.touched[field.name] &&
                form.errors[field.name] && (
                  <ValidateError>{form.errors[field.name]}</ValidateError>
                )
              : ''}
          </>
        ) : (
          <>
            <input
              ref={fileRef}
              id={id || uuid}
              {...otherProps}
              className={`text-input ${
                icon ? 'with-icon' : ''
              } ${`icon-${iconPosition}`} ${
                large ? 'large' : ''
              } ${classNameInput}`}
              maxLength={maxLength || ''}
              type={type || 'text'}
              disabled={disabled}
              required={required}
              onChange={(el) => {
                if (type === 'file') {
                  const convertFileListIntoArray = [...el.currentTarget.files];
                  const filteredFilesByType = convertFileListIntoArray.filter(
                    (file) =>
                      file.type.includes('image/') || file.type.includes('pdf')
                  );

                  if (filteredFilesByType.length) {
                    setShowCircle(true);
                  } else {
                    setShowCircle(false);
                  }
                }

                if (type === 'number' && max && !Number.isNaN(min)) {
                  handleError(el.target.value);
                }

                return handleChange(el);
              }}
            />
            {icon ? (
              <i className={`${icon} ${iconPosition} ${color || ''}`} />
            ) : (
              ''
            )}
            {type === 'file' && showCircle && (
              <Icons path={mdiCheckCircle} color="var(--success)" />
            )}
            {error}
            {err && (
              <p className={`fw-bold text-error ${classNameNumberError}`}>
                Favor inserir um valor de {min} até {max}.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};
